import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { depositsAPI } from '../lib/api';
import { receiptUrl } from '../components/admin/BankTransferReceiptFields';
import { useIssuerReference } from '../components/dashboard/WithdrawalTimer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Landmark, Loader2, Paperclip, Wallet, PiggyBank, Hash, ChevronRight, FileText, Download, ExternalLink } from 'lucide-react';

const fmt = (a, c) => `${c === 'USD' ? '$' : '€'}${Number(a || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
    <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-4 flex items-start gap-3" data-testid={testId}>
        <div className="w-10 h-10 rounded-xl bg-[#1973B8]/15 border border-[#1973B8]/30 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-[#7CB1E5]" />
        </div>
        <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-xl font-bold text-white tabular-nums">{value}</p>
            {sub && <p className="text-xs text-slate-500">{sub}</p>}
        </div>
    </div>
);

const DepositDetailDialog = ({ depositId, onClose }) => {
    const issuerRef = useIssuerReference();
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!depositId) return;
        setData(null);
        depositsAPI.detail(depositId).then((r) => setData(r.data)).catch(() => { toast.error('No se pudo cargar el detalle'); onClose(); });
    }, [depositId, onClose]);
    const d = data?.deposit;
    const r = data?.receipt;
    return (
        <Dialog open={!!depositId} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white" data-testid="deposit-detail-dialog">
                <DialogHeader><DialogTitle className="text-white">Detalle del depósito</DialogTitle></DialogHeader>
                {!d ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#7CB1E5]" /></div> : (
                    <div className="space-y-4">
                        <div className="rounded-xl bg-slate-950 border border-slate-800 p-4">
                            <p className="text-3xl font-bold text-emerald-400 tabular-nums" data-testid="deposit-detail-amount">+{fmt(d.amount, d.currency)} <span className="text-base text-slate-400">{d.currency}</span></p>
                            <p className="text-xs text-slate-500 mt-1">{fmtDate(d.created_at)}</p>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div>
                                <p className="text-[11px] uppercase tracking-wider text-slate-500">Concepto</p>
                                <p className="text-slate-200 whitespace-pre-line" data-testid="deposit-detail-concept">{d.display_description || d.description}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">Referencia</p><p className="text-slate-200 font-mono text-xs" data-testid="deposit-detail-ref">{d.transaction_reference || '—'}</p></div>
                                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">Estado</p><p className="text-emerald-300 text-xs font-semibold">Acreditado</p></div>
                                <div className="col-span-2"><p className="text-[11px] uppercase tracking-wider text-slate-500">Emisor</p><p className="text-[#7CB1E5] text-xs font-semibold" data-testid="deposit-detail-issuer">{issuerRef}</p></div>
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Comprobante bancario</p>
                                {r ? (
                                    <div className="flex items-center gap-2 rounded-lg bg-slate-950 border border-slate-800 px-3 py-2" data-testid="deposit-detail-receipt">
                                        <FileText className="w-4 h-4 text-[#7CB1E5]" />
                                        <span className="text-xs text-slate-200 truncate flex-1">{r.filename}</span>
                                        <span className="text-[10px] text-slate-500">{r.size ? `${(r.size / 1024).toFixed(0)} KB` : ''}</span>
                                        <a href={receiptUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-white" title="Abrir" data-testid="deposit-detail-receipt-open"><ExternalLink className="w-4 h-4" /></a>
                                        <a href={receiptUrl(r.file_id, true)} className="text-slate-300 hover:text-white" title="Descargar" data-testid="deposit-detail-receipt-download"><Download className="w-4 h-4" /></a>
                                    </div>
                                ) : <p className="text-xs text-slate-500" data-testid="deposit-detail-no-receipt">Sin comprobante adjunto para este depósito.</p>}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

const DepositHistoryPage = () => {
    const issuerReference = useIssuerReference();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        depositsAPI.history().then((r) => setData(r.data)).catch(() => toast.error('Error al cargar el historial de depósitos')).finally(() => setLoading(false));
    }, []);

    const deposits = data?.deposits || [];
    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-6" data-testid="deposit-history-page">
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-[#1973B8]/15 border border-[#1973B8]/30">
                        <Landmark className="w-6 h-6 text-[#7CB1E5]" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Historial de Depósitos</h1>
                        <p className="text-sm text-slate-400 font-light">Desglose individual de cada acreditación realizada en tu cuenta</p>
                    </div>
                </div>

                {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#7CB1E5]" /></div> : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <StatCard icon={Wallet} label="Saldo total acumulado" testId="deposit-stat-balance"
                                value={fmt(data?.balance_eur, 'EUR')} sub={data?.balance_usd ? `${fmt(data.balance_usd, 'USD')} USD` : 'Saldo disponible actual'} />
                            <StatCard icon={PiggyBank} label="Total acreditado" testId="deposit-stat-total"
                                value={fmt(data?.total_credited_eur, 'EUR')} sub={data?.total_credited_usd ? `${fmt(data.total_credited_usd, 'USD')} USD` : 'Suma de todas las acreditaciones'} />
                            <StatCard icon={Hash} label="Depósitos" testId="deposit-stat-count"
                                value={data?.count ?? 0} sub={`${data?.with_receipt ?? 0} con comprobante bancario`} />
                        </div>

                        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden">
                            <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                                <p className="text-white font-semibold text-sm">Acreditaciones</p>
                                <p className="text-[11px] text-slate-500">Cada acreditación se suma al saldo · registros inalterables</p>
                            </div>
                            {deposits.length === 0 ? (
                                <p className="text-slate-600 text-sm text-center py-12" data-testid="deposit-empty">Aún no tienes depósitos acreditados.</p>
                            ) : (
                                <div className="divide-y divide-slate-800/60">
                                    {deposits.map((d) => (
                                        <button key={d.id} onClick={() => setSelected(d.id)}
                                            className="w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-slate-800/40 transition-colors"
                                            data-testid={`deposit-item-${d.id}`}>
                                            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center flex-shrink-0">
                                                <Landmark className="w-4 h-4 text-emerald-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-slate-200 text-xs sm:text-sm whitespace-pre-line leading-snug">{d.display_description || d.description}</p>
                                                <p className="text-[11px] text-slate-500 mt-0.5">{fmtDate(d.created_at)} · Ref. {d.transaction_reference || '—'}</p>
                                                {issuerReference && <p className="text-[10.5px] text-[#7CB1E5] mt-0.5" data-testid={`deposit-issuer-${d.id}`}>{issuerReference}</p>}
                                            </div>
                                            <div className="flex items-center gap-3 flex-shrink-0">
                                                {d.receipt_file_id && <span className="inline-flex items-center gap-1 text-[10.5px] text-[#7CB1E5]" title="Comprobante adjunto"><Paperclip className="w-3 h-3" /> Comprobante</span>}
                                                <p className="text-emerald-400 font-bold tabular-nums text-sm sm:text-base">+{fmt(d.amount, d.currency)} <span className="text-[10px] text-slate-500">{d.currency}</span></p>
                                                <ChevronRight className="w-4 h-4 text-slate-600" />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
                <DepositDetailDialog depositId={selected} onClose={() => setSelected(null)} />
            </div>
        </Layout>
    );
};

export default DepositHistoryPage;
